import React from 'react';
import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl-next';

import CalendarIcon from '@atlaskit/icon/core/calendar';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import EditIcon from '@atlaskit/icon/core/edit';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import ListChecklistIcon from '@atlaskit/icon/core/list-checklist';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import PersonIcon from '@atlaskit/icon/core/person';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import TargetIcon from '@atlaskit/icon/core/target';
import TaskIcon from '@atlaskit/icon/core/task';

import { HandleActionType } from '@atlassian/rovo-conversation-actions';

/**
 * Internationalized messages for create quick action titles
 */
const i18n = defineMessages({
	// Product Management
	defineRequirements: {
		id: 'experiment-rovo-chat-create-quick-actions.defineRequirements',
		defaultMessage: 'Define product requirements',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create product requirements.',
	},
	identifyStakeholders: {
		id: 'experiment-rovo-chat-create-quick-actions.identifyStakeholders',
		defaultMessage: 'Identify stakeholders',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a stakeholder map.',
	},
	gatherInsights: {
		id: 'experiment-rovo-chat-create-quick-actions.gatherInsights',
		defaultMessage: 'Gather insights',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create user insights.',
	},
	// Engineering
	sketchPlan: {
		id: 'experiment-rovo-chat-create-quick-actions.sketchPlan',
		defaultMessage: 'Sketch a plan',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a technical plan.',
	},
	documentDecisions: {
		id: 'experiment-rovo-chat-create-quick-actions.documentDecisions',
		defaultMessage: 'Document decisions',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a decision document.',
	},
	draftApiDocs: {
		id: 'experiment-rovo-chat-create-quick-actions.draftApiDocs',
		defaultMessage: 'Draft API docs',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create API documentation.',
	},
	// Marketing
	gtmPlan: {
		id: 'experiment-rovo-chat-create-quick-actions.gtmPlan',
		defaultMessage: 'Go-to-market plan',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a go-to-market plan.',
	},
	competitorAnalysis: {
		id: 'experiment-rovo-chat-create-quick-actions.competitorAnalysis',
		defaultMessage: 'Competitor analysis',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a competitor analysis.',
	},
	contentIdeas: {
		id: 'experiment-rovo-chat-create-quick-actions.contentIdeas',
		defaultMessage: 'Content ideas',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create content ideas.',
	},
	// Sales
	discoveryQuestions: {
		id: 'experiment-rovo-chat-create-quick-actions.discoveryQuestions',
		defaultMessage: 'Discovery questions',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create sales discovery questions.',
	},
	salesTimeline: {
		id: 'experiment-rovo-chat-create-quick-actions.salesTimeline',
		defaultMessage: 'Sales timeline',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a sales timeline.',
	},
	introEmail: {
		id: 'experiment-rovo-chat-create-quick-actions.introEmail',
		defaultMessage: 'Intro email',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create an introduction email.',
	},
	// Program Management
	projectPlan: {
		id: 'experiment-rovo-chat-create-quick-actions.projectPlan',
		defaultMessage: 'Project plan',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a project plan.',
	},
	riskAssessment: {
		id: 'experiment-rovo-chat-create-quick-actions.riskAssessment',
		defaultMessage: 'Risk assessment',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a risk assessment.',
	},
	statusReport: {
		id: 'experiment-rovo-chat-create-quick-actions.statusReport',
		defaultMessage: 'Status report',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a status report.',
	},
	// Other/General
	analyzeFeedback: {
		id: 'experiment-rovo-chat-create-quick-actions.analyzeFeedback',
		defaultMessage: 'Analyze feedback',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a feedback analysis.',
	},
	brainstormIdeas: {
		id: 'experiment-rovo-chat-create-quick-actions.brainstormIdeas',
		defaultMessage: 'Brainstorm ideas',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create ideas.',
	},
	researchPlan: {
		id: 'experiment-rovo-chat-create-quick-actions.researchPlan',
		defaultMessage: 'Research plan',
		description:
			'Title of a quick action in Rovo chat. Clicking this starts a conversation to create a research plan.',
	},
});

/**
 * Job title categories for prompt targeting
 */
export type JobTitleCategory =
	| 'engineering'
	| 'productManagement'
	| 'marketing'
	| 'sales'
	| 'programManagement'
	| 'other';

/**
 * Configuration for a job title category-based prompt
 */
export type JobTitleBasedPrompt = {
	id: string;
	title: MessageDescriptor;
	prompt: string;
	userJobTitleCategory: JobTitleCategory;
	icon: React.ComponentType<{ label: string }>;
};

/**
 * Child action generated from a job title category-based prompt
 * Used by the conversation assistant UI
 */
export type JobTitleBasedChildAction = {
	id: string;
	title: string;
	icon: () => React.ReactElement;
	handleType: HandleActionType;
	dynamicPrompt: string;
};

/**
 * Create related quick actions prompts configuration
 * Organized by job title category with top 3 prompts per category
 */
export const CREATE_QUICK_ACTIONS_PROMPTS: JobTitleBasedPrompt[] = [
	// Product Management
	{
		id: 'define-requirements',
		title: i18n.defineRequirements,
		prompt:
			'Help me define clear product requirements for a new feature, including user stories, acceptance criteria, and success metrics.',
		userJobTitleCategory: 'productManagement',
		icon: ListChecklistIcon,
	},
	{
		id: 'identify-stakeholders',
		title: i18n.identifyStakeholders,
		prompt:
			'Help me identify key stakeholders for this project and create a stakeholder map with their interests, influence, and communication needs.',
		userJobTitleCategory: 'productManagement',
		icon: PeopleGroupIcon,
	},
	{
		id: 'gather-insights',
		title: i18n.gatherInsights,
		prompt:
			'Help me gather and synthesize user insights from research data, identifying key patterns, pain points, and opportunities.',
		userJobTitleCategory: 'productManagement',
		icon: LightbulbIcon,
	},

	// Engineering
	{
		id: 'sketch-plan',
		title: i18n.sketchPlan,
		prompt:
			'Help me sketch out a technical plan including architecture decisions, milestones, and potential risks to consider.',
		userJobTitleCategory: 'engineering',
		icon: RoadmapIcon,
	},
	{
		id: 'document-decisions',
		title: i18n.documentDecisions,
		prompt:
			'Help me document a technical decision using an ADR (Architecture Decision Record) format, including context, options considered, and rationale.',
		userJobTitleCategory: 'engineering',
		icon: EditIcon,
	},
	{
		id: 'draft-api-docs',
		title: i18n.draftApiDocs,
		prompt:
			'Help me draft comprehensive API documentation including endpoints, request/response formats, authentication, and example use cases.',
		userJobTitleCategory: 'engineering',
		icon: ListChecklistIcon,
	},

	// Marketing
	{
		id: 'gtm-plan',
		title: i18n.gtmPlan,
		prompt:
			'Help me create a go-to-market plan including target audience, messaging, channels, timeline, and success metrics.',
		userJobTitleCategory: 'marketing',
		icon: RoadmapIcon,
	},
	{
		id: 'competitor-analysis',
		title: i18n.competitorAnalysis,
		prompt:
			'Help me analyze competitors including their positioning, strengths, weaknesses, and market opportunities.',
		userJobTitleCategory: 'marketing',
		icon: ChartTrendUpIcon,
	},
	{
		id: 'content-ideas',
		title: i18n.contentIdeas,
		prompt:
			'Help me brainstorm content ideas for our marketing campaigns, including blog posts, social media, and email sequences.',
		userJobTitleCategory: 'marketing',
		icon: MegaphoneIcon,
	},

	// Sales
	{
		id: 'discovery-questions',
		title: i18n.discoveryQuestions,
		prompt:
			'Help me create effective discovery questions to understand prospect needs, challenges, and decision criteria.',
		userJobTitleCategory: 'sales',
		icon: PersonIcon,
	},
	{
		id: 'sales-timeline',
		title: i18n.salesTimeline,
		prompt:
			'Help me create a sales timeline with key milestones, touchpoints, and follow-up activities for this deal.',
		userJobTitleCategory: 'sales',
		icon: CalendarIcon,
	},
	{
		id: 'intro-email',
		title: i18n.introEmail,
		prompt:
			'Help me draft a compelling introduction email that grabs attention and clearly communicates our value proposition.',
		userJobTitleCategory: 'sales',
		icon: TargetIcon,
	},

	// Program Management
	{
		id: 'project-plan',
		title: i18n.projectPlan,
		prompt:
			'Help me create a comprehensive project plan including scope, timeline, resources, dependencies, and milestones.',
		userJobTitleCategory: 'programManagement',
		icon: TaskIcon,
	},
	{
		id: 'risk-assessment',
		title: i18n.riskAssessment,
		prompt:
			'Help me identify and assess project risks, including likelihood, impact, and mitigation strategies.',
		userJobTitleCategory: 'programManagement',
		icon: ChartTrendUpIcon,
	},
	{
		id: 'status-report',
		title: i18n.statusReport,
		prompt:
			'Help me create a status report summarizing progress, blockers, next steps, and key decisions needed.',
		userJobTitleCategory: 'programManagement',
		icon: ListChecklistIcon,
	},

	// Other/General
	{
		id: 'analyze-feedback',
		title: i18n.analyzeFeedback,
		prompt:
			'Help me analyze feedback data to identify trends, common themes, and actionable insights.',
		userJobTitleCategory: 'other',
		icon: ChartTrendUpIcon,
	},
	{
		id: 'brainstorm-ideas',
		title: i18n.brainstormIdeas,
		prompt:
			'Help me brainstorm creative ideas and solutions, exploring different perspectives and possibilities.',
		userJobTitleCategory: 'other',
		icon: LightbulbIcon,
	},
	{
		id: 'research-plan',
		title: i18n.researchPlan,
		prompt:
			'Help me create a research plan including objectives, methodology, participants, and analysis approach.',
		userJobTitleCategory: 'other',
		icon: ListChecklistIcon,
	},
];

/**
 * Filters create quick action prompts by user job title category
 */
export function getPromptsForJobTitleCategory(
	userJobTitleCategory: JobTitleCategory,
): JobTitleBasedPrompt[] {
	return CREATE_QUICK_ACTIONS_PROMPTS.filter(
		(prompt) => prompt.userJobTitleCategory === userJobTitleCategory,
	);
}

/**
 * Converts create quick action prompts to child actions format
 */
export function getCreateQuickActionsChildren(
	prompts: JobTitleBasedPrompt[],
	intl: IntlShape,
): JobTitleBasedChildAction[] {
	return prompts.map((prompt) => ({
		id: `${prompt.id}-rovo-quick-action`,
		title: intl.formatMessage(prompt.title),
		icon: () => <prompt.icon label="" />,
		handleType: HandleActionType.Chat,
		dynamicPrompt: prompt.prompt,
	}));
}

/**
 * Keyword patterns for each job title category
 */
const JOB_TITLE_KEYWORDS: Record<Exclude<JobTitleCategory, 'other'>, string[]> = {
	engineering: [
		'engineer',
		'developer',
		'architect',
		'sre',
		'devops',
		'software',
		'technical lead',
		'tech lead',
	],
	productManagement: ['product manager', 'product owner', 'product lead', 'pm '],
	marketing: ['marketing', 'brand', 'content', 'communications', 'growth'],
	sales: ['sales', 'account executive', 'account manager', 'business development', 'bdr', 'sdr'],
	programManagement: [
		'program manager',
		'project manager',
		'delivery manager',
		'scrum master',
		'agile coach',
		'pmo',
	],
};

/**
 * Maps a job title string to a JobTitleCategory
 * Uses keyword matching to determine the category
 */
export function mapJobTitleToCategory(jobTitle: string | null): JobTitleCategory {
	if (!jobTitle) {
		return 'other';
	}

	const normalizedTitle = jobTitle.toLowerCase();

	// Safe to iterate: fixed set of 5 categories with ~30 total keywords.
	// Not user-controlled data, so no risk of quadratic performance issues.
	// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
	for (const [category, keywords] of Object.entries(JOB_TITLE_KEYWORDS)) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		if (keywords.some((keyword) => normalizedTitle.includes(keyword))) {
			return category as JobTitleCategory;
		}
	}

	return 'other';
}

/**
 * Wrapper that maps a job title to quick action children.
 */
export function getChildrenActionsForJobTitle(
	jobTitle: string | null,
	intl: IntlShape,
): JobTitleBasedChildAction[] {
	const category = mapJobTitleToCategory(jobTitle);
	const prompts = getPromptsForJobTitleCategory(category);
	return getCreateQuickActionsChildren(prompts, intl);
}
